import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import Footer from "../../components/Footer/Footer";
import BannerImage from "../../components/assets/about/CompanyPhoto.png"
import CompanyPhoto from "../../components/assets/about/CompanyPhoto.png";
import TaasentMission from "../../components/assets/about/TaasnetMission.png";
import TaasentVision from "../../components/assets/about/TaasnetVision.png";

import FourSuits from "./Suits/FourSuits";
import LandingHeader from "./LandingHeader";
// CoreValuesGrid.tsx
import {styled, Card } from "@mui/material";
import PeopleAltRounded   from "@mui/icons-material/PeopleAltRounded";
import VisibilityRounded  from "@mui/icons-material/VisibilityRounded";
import PublicRounded      from "@mui/icons-material/PublicRounded";
import FlashOnRounded     from "@mui/icons-material/FlashOnRounded";

const values = [
  { label: "Audience-First",        Icon: PeopleAltRounded },
  { label: "Radical Transparency",  Icon: VisibilityRounded },
  { label: "Global Inclusivity",    Icon: PublicRounded },
  { label: "Bias for Action",       Icon: FlashOnRounded },
];

// ---- design tokens (adapt to your theme file if you centralize colors) ----
const PURPLE_700   = "#4C1D95";  // brand text
const PURPLE_50    = "#F3F0FF";  // card bg
const PURPLE_100   = "#E9E4FF";  // hover bg
const SHADOW       = "0 4px 14px rgba(0,0,0,0.08)";

// ---- styled helpers ----
const ValueCard = styled(Card)(({ theme }) => ({
  background: PURPLE_50,
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "none",
  textAlign: "center",
  transition: "transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    background: PURPLE_100,
    boxShadow: SHADOW,
  },
}));

const AboutPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* shared neutral card style */
  const panelStyle = {
    position: "relative",
    mb: 16,
    px: { xs: 3, md: 6 },
    py: { xs: 8, md: 10 },
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 4px 18px rgba(0,0,0,0.04)",
  } as const;

  /* reusable radial accent */
  const blurAccent = (pos: "top" | "bottom", lr: "left" | "right") => ({
    position: "absolute",
    [pos]: -60,
    [lr]: -60,
    width: 200,
    height: 200,
    background: "radial-gradient(circle, rgba(159,3,255,0.3), transparent 70%)",
    filter: "blur(80px)",
    zIndex: 0,
  });

  return (
    <Box sx={{ fontFamily: '"Lexend", sans-serif', backgroundColor: "#fafafa" }}>
      {/* SEO */}
      <Helmet>
        <title>About StageLink | Live Talent Experiences</title>
        <title>About StageLink | Live Talent Experiences</title>
        <meta
          name="description"
          content="Learn about StageLink — a platform transforming real-time talent interaction across entertainment, education, events, and commerce."
        />
        <link rel="canonical" href="https://www.taasnet.com/about" />
        <meta property="og:title" content="About StageLink | Live Talent Experiences" />
        <meta
          property="og:description"
          content="Learn about StageLink — a platform transforming real-time talent interaction across entertainment, education, events, and commerce."
        />
        <meta property="og:image" content={BannerImage} />
      </Helmet>

      <LandingHeader />
<Box
  component="header"
  sx={{
    // vertical rhythm scales up with breakpoints
    py: { xs: 8, sm: 10, md: 14, lg: 18 },
    px: 2,
    // backdrop with a 55 % dark overlay for legibility
    backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${BannerImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    textAlign: "center",
    color: "#fff",
  }}
>
  {/* main heading */}
  <Typography
    component="h1"
    sx={{
      fontFamily: "Lexend, sans-serif",
      fontWeight: 800,
      lineHeight: 1.1,
      // fluid font sizes
      fontSize: { xs: 36, sm: 48, md: 64, lg: 72 },
      mb: { xs: 2, md: 3 },
    }}
  >
   &nbsp;StageLink
  </Typography>

  {/* sub-heading */}
  <Typography
    sx={{
      maxWidth: { xs: "100%", sm: "80%", md: "60%" },
      mx: "auto",
      fontFamily: "Lexend, sans-serif",
      fontSize: { xs: 16, sm: 18, md: 20, lg: 22 },
      opacity: 0.9,
    }}
  >
    Transforming real-time talent interaction through technology, passion, and purpose.
  </Typography>
</Box>


      <Container component="main" maxWidth="lg" sx={{ py: 8 }}>
        {/* Who We Are */}
        <Box component="section" id="who-we-are" sx={panelStyle}>
          <Box sx={blurAccent("top", "right")} />
          <Grid container spacing={6} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src={CompanyPhoto}
                alt="StageLink team"
                sx={{
                  width: "100%",
                  maxWidth: 460,
                  mx: "auto",
                  borderRadius: 4,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                  display: "block",
                }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="h3" fontWeight={800} sx={{ color: "#2E1065", mb: 2 ,fontFamily: '"Lexend", sans-serif', }}>
                Who&nbsp;We&nbsp;Are
              </Typography>
              <Typography sx={{ color: "#4B445C", fontSize: 20, lineHeight: 1.8, mb: 3 ,  fontFamily: '"Lexend", sans-serif',}}>
                Founded in <b>2025</b>, StageLink is a Seoul-based team of product builders,
                creative technologists, and talent advocates on a mission to remove every barrier
                between passion and access. From our first beta session in a small studio to
                <i> thousands </i> of live interactions monthly, we’ve stayed obsessed with
                real-time, authentic connection.
              </Typography>
           <Grid container spacing={{ xs: 2, md: 3 }}>
      {values.map(({ label, Icon }) => (
        <Grid key={label} item xs={6} md={3}>
          <ValueCard elevation={0}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Icon sx={{ fontSize: 40, color: PURPLE_700 }} />
              <Typography
                fontFamily="Lexend"
                fontWeight={700}
                sx={{ color: PURPLE_700,   fontFamily: '"Lexend", sans-serif', fontSize: { xs: 14, md: 16 } }}
              >
                {label}
              </Typography>
            </Box>
          </ValueCard>
        </Grid>
      ))}
    </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Mission */}
        <Box component="section" id="mission" sx={panelStyle}>
          <Box sx={blurAccent("bottom", "right")} />
          <Grid container spacing={6} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Grid item xs={12} md={6}>
              
              <Typography variant="h3" fontWeight={800} sx={{ color: "#2E1065", mb: 3 , fontFamily: '"Lexend", sans-serif' }}>
                Our Mission
              </Typography>
              
             
              <Typography sx={{ color: "#444", fontSize: 20, lineHeight: 1.8, maxWidth: 600 , fontFamily: '"Lexend", sans-serif' }}>
       
                <Box component="span"  fontWeight={600} color="#9F03FF" fontFamily='"Lexend", sans-serif'>
                  StageLink
                </Box>{" "}
                (Talent-as-a-Service Network) empowers global access to talent by removing borders
                and barriers. From{" "}
                <Box component="span" fontWeight={600}  fontFamily='"Lexend", sans-serif'>
                  mentorship
                </Box>{" "}
                and{" "}
                <Box component="span" fontWeight={600}  fontFamily='"Lexend", sans-serif'>
                  entertainment
                </Box>{" "}
                to{" "}
                <Box component="span" fontWeight={600}  fontFamily='"Lexend", sans-serif'>
                  live shopping
                </Box>{" "}
                and{" "}
                <Box component="span" fontWeight={600}  fontFamily='"Lexend", sans-serif'>
                  learning experiences
                </Box>{" "}
                — we bring it all together in personalised, real-time sessions built around you.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={TaasentMission}
                alt="Mission illustration"
                fontFamily='"Lexend", sans-serif'
                sx={{
                  width: "100%",
                  maxWidth: 480,
                  mx: "auto",
                  borderRadius: 4,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                  display: "block",
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Vision */}
        <Box component="section" id="vision" sx={panelStyle}>
          <Box sx={blurAccent("top", "left")} />
          <Grid container spacing={6} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Grid item xs={12} md={7}>
              <Typography variant="h3" fontWeight={800} sx={{ color: "#2E1065", mb: 3  , fontFamily: '"Lexend", sans-serif' }}>
                Our Vision
              </Typography>
              <Typography sx={{ color: "#4B445C", fontSize: 20, lineHeight: 1.8, maxWidth: 600 , fontFamily: '"Lexend", sans-serif' }}>
                We envision a world where{" "}
                <Box component="span" fontWeight={600} color="#9F03FF" fontFamily='"Lexend", sans-serif'>
                  passion meets access
                </Box>{" "}
                — no middlemen, no gatekeepers. A world where{" "}
                <Box component="span" fontWeight={600} fontFamily='"Lexend", sans-serif'>
                  learners, fans, buyers, and dreamers
                </Box>{" "}
                connect directly with the world’s best —{" "}
                <Box component="span" fontWeight={600} color="#9F03FF" fontFamily='"Lexend", sans-serif'>
                  live and in real time
                </Box>
                .
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src={TaasentVision}
                alt="Vision illustration"
                sx={{
                  width: "100%",
                  maxWidth: 480,
                  mx: "auto",
                  borderRadius: 4,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                  display: "block",
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Four Suits */}
        <FourSuits />

        {/* CTA */}
        <Box component="section" id="cta" sx={{ ...panelStyle, mt: 14, textAlign: "center" }}>
          <Box
            sx={{
              position: "absolute",
              top: -80,
              right: -80,
              width: 240,
              height: 240,
              background: "radial-gradient(circle, rgba(159,3,255,0.18), transparent 70%)",
              filter: "blur(100px)",
              zIndex: 0,
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography variant="h4" fontWeight={800} sx={{ color: "#2E1065", mb: 2 , fontFamily: '"Lexend", sans-serif' }}>
              Be part of the experience revolution.
            </Typography>
            <Typography
              sx={{ fontSize: 18, color: "#4B445C", mb: 4, maxWidth: 640, mx: "auto", fontFamily: '"Lexend", sans-serif' }}
            >
              Join a movement redefining how talents and audiences connect in real time. No
              middlemen. No barriers. Just meaningful interactions.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/register")}
              sx={{

                background: "linear-gradient(90deg, #9F03FF 0%, #7C00CC 100%)",
                color: "#fff",
                fontWeight: 700,
                borderRadius: 9999,
                px: 5,
                py: 1.75,
                fontSize: 16,
                fontFamily: '"Lexend", sans-serif',
                boxShadow: "0 6px 18px rgba(159,3,255,0.25)",
                transition: "all .3s ease",
                "&:hover": {
                  background: "linear-gradient(90deg, #8500d1 0%, #6900b2 100%)",
                  boxShadow: "0 8px 20px rgba(159,3,255,0.3)",
                },
              }}
            >
              Join StageLink
            </Button>
          </Box>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default AboutPage;
